import { useCallback, useMemo } from "react";
import { useStore, useStoreContext } from "ra-core";

import type {
  CampaignDef,
  ContactListDef,
  DashboardDef,
  CustomFieldDef,
  DealStage,
  EmailTemplate,
  LabeledValue,
  NoteStatus,
  IntegrationConfig,
  ScoringRule,
  SequenceDef,
  SidebarModulePref,
  SignalsConfig,
  SlateForm,
  WorkflowDef,
} from "../types";
import type { PlanLimits } from "@slate/shared";
import { defaultConfiguration, defaultSignals } from "./defaultConfiguration";

export interface CompanyProfile {
  companyName: string;
  websiteUrl: string;
  industry: string;
  phone: string;
}

export const CONFIGURATION_STORE_KEY = "app.configuration";

export interface ConfigurationContextValue {
  companySectors: LabeledValue[];
  currency: string;
  dealCategories: LabeledValue[];
  dealPipelineStatuses: string[];
  dealStages: DealStage[];
  customFields: CustomFieldDef[];
  forms: SlateForm[];
  emailTemplates: EmailTemplate[];
  /** Physical mailing address, required in every marketing email footer (CAN-SPAM). */
  mailingAddress: string;
  /**
   * This workspace's own verified Postmark sending address for marketing/
   * sequence email (e.g. "hello@acme.com") - Sequences/Workflows
   * sends never fall back to Grey Matter's shared system address, so
   * sending stays off for this workspace until its own domain is verified
   * in Postmark and set here.
   */
  marketingFromEmail: string;
  /**
   * The sender NAME shown with marketingFromEmail, e.g. "Acme Co" ->
   * From: "Acme Co" <hello@acme.com>. Blank = the bare address,
   * which most inboxes show as just "hello".
   */
  marketingFromName: string;
  /**
   * Track opens and clicks on marketing email (decision #108) - Postmark
   * reports them back as email.opened / email.clicked on the contact.
   */
  marketingTrackEngagement: boolean;
  /**
   * Read-only display copy of the domain a Grey Matter super admin has
   * approved for this workspace's marketing sends (decision #79), pushed
   * here from the control plane - editing this locally changes nothing,
   * since the real check happens server-side against the control plane's
   * own value, never this copy. Blank means nothing has been approved yet.
   */
  marketingApprovedDomain: string;
  /** Cloudflare Turnstile site key; enables CAPTCHA on forms that opt in. */
  turnstileSiteKey: string;
  contactLists: ContactListDef[];
  sequences: SequenceDef[];
  campaigns: CampaignDef[];
  dashboards: DashboardDef[];
  /** Order + visibility of the company record's about-rail modules. */
  companySidebarModules: SidebarModulePref[];
  /** Native lead-scoring rules (signal → points). */
  scoringRules: ScoringRule[];
  /** Trigger → action automations, executed by the Phase 4 engine. */
  workflows: WorkflowDef[];
  integrations: Record<string, IntegrationConfig>;
  noteStatuses: NoteStatus[];
  /** Workspace timezone (IANA). All logged times display in this zone. */
  timezone: string;
  /** Who this workspace belongs to - shown on the Company profile card. */
  companyProfile: CompanyProfile;
  /**
   * Plan entitlements (seats, contacts). Managed by Grey Matter from the
   * operator console - read-only inside the tenant app. Seeded at
   * provisioning (defaults: 5 users, 1,000 contacts).
   */
  planLimits: PlanLimits;
  /**
   * Per-tenant feature visibility (e.g. hide Reports until a client is
   * ready for it). Managed by Grey Matter from the operator console's
   * instance detail screen - read-only inside the tenant app, same as
   * planLimits. Absent key or anything but an explicit `false` means
   * visible, so existing tenants with no moduleFlags at all see everything,
   * unchanged.
   */
  moduleFlags?: Record<string, boolean>;
  /** Thresholds for the dashboard's "Keep an eye on" panel. */
  signals: SignalsConfig;
  /** Ordered funnel ladder for contacts (Subscriber → … → Customer). */
  lifecycleStages: LabeledValue[];
  /** Why deals are lost - offered in the close dialog. */
  closeReasons: LabeledValue[];
  taskTypes: LabeledValue[];
  title: string;
  darkModeLogo: string;
  lightModeLogo: string;
}

export const useConfigurationContext = () => {
  const [config] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
    defaultConfiguration,
  );
  // Merge with defaults so that missing fields in stored config
  // fall back to default values (e.g. when new settings are added)
  return useMemo(() => ({ ...defaultConfiguration, ...config }), [config]);
};

/**
 * The "Keep an eye on" thresholds, deep-merged with defaults so configs
 * saved before a new knob existed still get every field.
 */
export const useSignals = (): SignalsConfig => {
  const { signals } = useConfigurationContext();
  return useMemo(
    () => ({
      heatingUp: { ...defaultSignals.heatingUp, ...signals?.heatingUp },
      slipping: { ...defaultSignals.slipping, ...signals?.slipping },
      overdue: { ...defaultSignals.overdue, ...signals?.overdue },
      perColumn: signals?.perColumn ?? defaultSignals.perColumn,
    }),
    [signals],
  );
};

/**
 * When each top-level configuration key was last changed by the user in this
 * tab (T2 in docs/automation-hardening.md). The startup loader keeps keys
 * edited while its fetch was in flight instead of overwriting them with the
 * older copy it fetched - which used to revert, then save, the old value.
 */
const localEdits = new Map<string, number>();

export const configKeysEditedSince = (since: number): string[] =>
  [...localEdits].filter(([, at]) => at >= since).map(([key]) => key);

export const useConfigurationUpdater = () => {
  const store = useStoreContext();
  const [, setConfig] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
  );
  return useCallback(
    (next: ConfigurationContextValue) => {
      const current = (store.getItem(CONFIGURATION_STORE_KEY) ?? {}) as Record<
        string,
        unknown
      >;
      const now = Date.now();
      for (const key of new Set([...Object.keys(current), ...Object.keys(next)])) {
        if (current[key] !== (next as unknown as Record<string, unknown>)[key]) {
          localEdits.set(key, now);
        }
      }
      setConfig(next);
    },
    [store, setConfig],
  );
};
